import { api } from "./api";

type Lottie = Record<string, unknown>;

/**
 * Loads a pack sticker's Lottie JSON with as little server work as possible:
 *   1. memory (this session)  ->  2. Cache Storage (survives app restarts)  ->  3. network.
 * A custom emoji id never changes its content, so once a device has it, the server is
 * never asked for it again. Network fetches are limited to a few in parallel.
 */
const MEM_MAX = 150;
const mem = new Map<string, Lottie>();
const inflight = new Map<string, Promise<Lottie>>();
const CACHE_NAME = "pack-tgs-v1";
const keyUrl = (id: string) => `https://tgs.local/${encodeURIComponent(id)}`;

const MAX_PARALLEL = 4;
let active = 0;
const waiting: Array<() => void> = [];
const acquire = () =>
  new Promise<void>((resolve) => {
    if (active < MAX_PARALLEL) {
      active++;
      resolve();
    } else waiting.push(() => { active++; resolve(); });
  });
const release = () => {
  active--;
  waiting.shift()?.();
};

function remember(id: string, json: Lottie) {
  mem.delete(id);
  mem.set(id, json);
  while (mem.size > MEM_MAX) mem.delete(mem.keys().next().value as string);
}

async function readStored(id: string): Promise<Lottie | null> {
  try {
    if (typeof caches === "undefined") return null;
    const hit = await (await caches.open(CACHE_NAME)).match(keyUrl(id));
    return hit ? ((await hit.json()) as Lottie) : null;
  } catch {
    return null;
  }
}

async function writeStored(id: string, json: Lottie) {
  try {
    if (typeof caches === "undefined") return;
    await (await caches.open(CACHE_NAME)).put(keyUrl(id), new Response(JSON.stringify(json)));
  } catch {
    /* storage may be full / blocked - the memory cache still works */
  }
}

export function loadTgs(id: string): Promise<Lottie> {
  const cached = mem.get(id);
  if (cached) return Promise.resolve(cached);
  const pending = inflight.get(id);
  if (pending) return pending;

  const p = (async () => {
    const stored = await readStored(id);
    if (stored) return stored;
    await acquire();
    try {
      const json = await api.get<Lottie>(`/api/packs/emoji/${encodeURIComponent(id)}/tgs`);
      void writeStored(id, json);
      return json;
    } finally {
      release();
    }
  })()
    .then((json) => {
      remember(id, json);
      return json;
    })
    .finally(() => inflight.delete(id));

  inflight.set(id, p);
  return p;
}

/** lottie-web mutates the data it is given, so every player gets its own copy. */
export function cloneLottie(json: Lottie): Lottie {
  return typeof structuredClone === "function" ? structuredClone(json) : JSON.parse(JSON.stringify(json));
}
